import { config } from '../config';
import { APIError, APIErrorException } from '../services/types/errors';
import { endSession } from './authSession';
import { signInPath } from './postAuthPath';

/**
 * Get auth headers for API requests
 * @returns Headers object with authorization token if available
 */
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

/**
 * Makes an authenticated API request
 * @param endpoint - The API endpoint to call
 * @param options - Request options
 * @returns Promise with response data
 */
export const fetchWithAuth = async (
  endpoint: string,
  options: RequestInit = {}
) => {
  const token = localStorage.getItem('token');

  const defaultHeaders = {
    'Content-Type': 'application/json'
  };

  // Only add default headers if we're not dealing with FormData
  const headers = options.body instanceof FormData
    ? { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), ...(options.headers || {}) }
    : { ...defaultHeaders, ...(token ? { 'Authorization': `Bearer ${token}` } : {}), ...(options.headers || {}) };

  const requestConfig = {
    ...options,
    headers
  };

  // Build the full URL - config.API_URL already includes /api
  const fullUrl = `${config.API_URL}${endpoint}`;

  const response = await fetch(fullUrl, requestConfig);

  // Handle 401 Unauthorized errors by redirecting to login
  if (response.status === 401) {
    // The hard navigation below reloads the document, so no in-memory cache
    // survives it and no query-cache or observability reset is needed here.
    endSession();

    // Redirect to sign-in, carrying where they were going
    window.location.href = signInPath(window.location.pathname, window.location.search);
    throw new Error('Session expired. Please login again.');
  }

  // Handle non-2xx responses
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'Request Failed',
      message: `Request failed with status ${response.status}`
    }));

    // Support both old format (detail) and new format (error, message).
    // Map backend's `errors` field to `details` for validation error handling.
    //
    // `detail` arrives in three shapes: a plain string (most raises), an object
    // (structured validators such as taxonomy draft save, which return
    // `{message, errors, warnings}`), or an array (FastAPI's own body errors).
    // Only the string and the object's own `message` are usable as display text;
    // previously an object `detail` won the `||` chain and was coerced to
    // "[object Object]" by the Error constructor, discarding the explanation.
    const rawDetail = errorData.detail;
    const detailMessage =
      typeof rawDetail === 'string'
        ? rawDetail
        : rawDetail !== null &&
          typeof rawDetail === 'object' &&
          typeof (rawDetail as { message?: unknown }).message === 'string'
          ? (rawDetail as { message: string }).message
          : undefined;

    const apiError: APIError = {
      error: errorData.error || 'Error',
      message: errorData.message || detailMessage || 'An error occurred',
      // Unchanged: v3 form screens read this as a field-name → message map.
      details: errorData.errors || errorData.details,
      // Verbatim, for consumers that understand the endpoint's detail shape.
      detail: rawDetail,
    };

    throw new APIErrorException(response.status, apiError);
  }

  // For 204 No Content responses, return null (common for DELETE operations)
  if (response.status === 204) {
    return null;
  }

  // For other successful responses, parse JSON
  return await response.json();
};

/**
 * Create an API client with pre-configured auth headers
 */
export const getApi = () => {
  return {
    get: async (endpoint: string, options: { headers?: Record<string, string> } = {}) => {
      const response = await fetchWithAuth(endpoint, {
        method: 'GET',
        ...options
      });
      return { data: response };
    },
    post: async (endpoint: string, data: any, options: { headers?: Record<string, string> } = {}) => {
      let body;
      let customOptions: { headers?: Record<string, string> } = {
        ...options,
        headers: { ...(options.headers || {}) }
      };

      // Handle FormData specially for file uploads
      if (data instanceof FormData) {
        body = data;
        // Don't set content-type for FormData - browser will set it with boundary
        // This is critical for multipart/form-data uploads

        // Create a new headers object without Content-Type
        const { 'Content-Type': _, ...headersWithoutContentType } = customOptions.headers || {};
        customOptions.headers = headersWithoutContentType;

        console.log("FormData detected, removing Content-Type header");
      } else {
        body = JSON.stringify(data);
        customOptions.headers = {
          ...(customOptions.headers || {}),
          'Content-Type': 'application/json'
        };
      }

      // Log the request for debugging
      console.log(`API Request: POST ${endpoint}`);
      console.log("Request body type:", data instanceof FormData ? "FormData" : typeof data);
      console.log("Headers:", customOptions.headers || {});

      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body,
        ...customOptions
      });

      return { data: response };
    },
    put: async (endpoint: string, data: any, options: { headers?: Record<string, string> } = {}) => {
      const response = await fetchWithAuth(endpoint, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options
      });
      return { data: response };
    },
    delete: async (endpoint: string, options: { headers?: Record<string, string> } = {}) => {
      const response = await fetchWithAuth(endpoint, {
        method: 'DELETE',
        ...options
      });
      return { data: response };
    }
  };
};

/**
 * Download a file from an authenticated endpoint
 * @param url - Full URL to download from
 * @param filename - Filename for the downloaded file
 */
export const downloadFileWithAuth = async (url: string, filename: string): Promise<void> => {
  // Validate URL origin to prevent JWT leakage to untrusted domains
  const resolvedUrl = url.startsWith('/') ? `${config.API_URL}${url}` : url;
  const allowedOrigin = new URL(config.API_URL).origin;
  const targetOrigin = new URL(resolvedUrl).origin;
  if (targetOrigin !== allowedOrigin && targetOrigin !== window.location.origin) {
    throw new Error(`Download blocked: untrusted origin ${targetOrigin}`);
  }

  const token = localStorage.getItem('token');
  const response = await fetch(resolvedUrl, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401) {
    endSession();
    window.location.href = signInPath(window.location.pathname, window.location.search);
    throw new Error('Session expired. Please login again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      detail: `Download failed with status ${response.status}`,
    }));
    throw new Error(errorData.detail || 'Download failed');
  }

  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = downloadUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(a);
};
